import { getActiveOperatorAssertion } from '../auth/activeOperatorSession.js';
import { activateSharedTabletOperatorGrant } from '../auth/sharedTabletOperatorApi.js';
import { getDeviceId } from '../db/local.js';
import { getSupabase, SUPABASE_URL } from '../supabase.js';
import {
  DocumentImportReviewSchema,
  DocumentImportReviewRequestPayloadSchema,
  type DocumentImportReview,
  type DocumentImportReviewRequestPayload,
} from './documentImportReviewTypes.js';

export {
  DocumentImportReviewSchema,
  DocumentImportReviewRequestPayloadSchema,
  type DocumentImportReview,
  type DocumentImportReviewRequestPayload,
};

export async function fetchDocumentImportReviews(input: {
  tenantId: string;
  businessId: string;
}): Promise<DocumentImportReview[]> {
  const { data, error } = await getSupabase()
    .from('document_import_reviews_v1')
    .select('id,tenant_id,business_id,doc_kind,status,submitted_by_user_id,reviewed_by_user_id,committed_document_id,rejection_reason,summary,created_at,updated_at')
    .eq('tenant_id', input.tenantId)
    .eq('business_id', input.businessId)
    .in('status', ['awaiting_review', 'rejected'])
    .order('created_at', { ascending: false })
    .limit(30);
  if (error) throw error;
  return (data ?? []).flatMap((row) => {
    const parsed = DocumentImportReviewSchema.safeParse(row);
    return parsed.success ? [parsed.data] : [];
  });
}

export async function fetchDocumentImportReviewPayload(input: {
  tenantId: string;
  reviewId: string;
}): Promise<{
  id: string;
  tenant_id: string;
  business_id: string;
  doc_kind: 'ddt' | 'fattura';
  status: 'awaiting_review' | 'approved' | 'rejected';
  submitted_by_user_id: string;
  request_payload: DocumentImportReviewRequestPayload;
}> {
  const { data, error } = await getSupabase()
    .from('document_import_reviews_v1')
    .select('id,tenant_id,business_id,doc_kind,status,submitted_by_user_id,request_payload')
    .eq('id', input.reviewId)
    .eq('tenant_id', input.tenantId)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('document_import_review_not_found');
  const payload = DocumentImportReviewRequestPayloadSchema.safeParse(data.request_payload);
  if (!payload.success) throw new Error('document_import_review_payload_invalid');
  return {
    id: data.id as string,
    tenant_id: data.tenant_id as string,
    business_id: data.business_id as string,
    doc_kind: data.doc_kind as 'ddt' | 'fattura',
    status: data.status as 'awaiting_review' | 'approved' | 'rejected',
    submitted_by_user_id: data.submitted_by_user_id as string,
    request_payload: payload.data,
  };
}

export async function approveDocumentImportReview(input: {
  tenantId: string;
  reviewId: string;
}): Promise<Record<string, unknown>> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('review_not_authenticated');
  const response = await fetch(
    `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/fiscal-photo`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tenant_id: input.tenantId,
        mode: 'approve_review',
        review_request_id: input.reviewId,
        device_id: await getDeviceId(),
        operator_assertion: getActiveOperatorAssertion() ?? undefined,
      }),
    },
  );
  const payload = await response.json() as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(String(payload.error ?? `review_approve_http_${response.status}`));
  }
  return payload;
}

export async function repairDocumentImportReviewOriginal(input: {
  tenantId: string;
  reviewId: string;
  replacementPagePaths: string[];
}): Promise<{ pages: number; path: string; mime: string }> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('review_not_authenticated');
  const response = await fetch(
    `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/fiscal-photo`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tenant_id: input.tenantId,
        mode: 'repair_review_original',
        review_request_id: input.reviewId,
        replacement_page_paths: input.replacementPagePaths,
        device_id: await getDeviceId(),
        operator_assertion: getActiveOperatorAssertion() ?? undefined,
      }),
    },
  );
  const payload = await response.json() as Record<string, unknown>;
  if (!response.ok) {
    if (payload.error === 'review_source_page_count_mismatch') {
      throw new Error(`review_source_page_count_mismatch:${payload.expected_pages ?? '?'}:${payload.received_pages ?? '?'}`);
    }
    throw new Error(String(payload.error ?? `review_original_repair_http_${response.status}`));
  }
  return {
    pages: Number(payload.pages ?? 0),
    path: String(payload.review_original_path ?? ''),
    mime: String(payload.review_original_mime ?? 'application/octet-stream'),
  };
}

async function updateReviewOriginal(input: {
  tenantId: string;
  reviewId: string;
  mode: 'defer_review_original' | 'attach_review_original';
  replacementPagePaths?: string[];
}): Promise<Record<string, unknown>> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('review_not_authenticated');
  const response = await fetch(
    `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/fiscal-photo`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tenant_id: input.tenantId,
        mode: input.mode,
        review_request_id: input.reviewId,
        replacement_page_paths: input.replacementPagePaths,
        device_id: await getDeviceId(),
        operator_assertion: getActiveOperatorAssertion() ?? undefined,
      }),
    },
  );
  const payload = await response.json() as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(String(payload.error ?? `review_original_update_http_${response.status}`));
  }
  return payload;
}

export async function deferDocumentImportReviewOriginal(input: {
  tenantId: string;
  reviewId: string;
}): Promise<void> {
  await updateReviewOriginal({ ...input, mode: 'defer_review_original' });
}

export async function attachDocumentImportReviewOriginal(input: {
  tenantId: string;
  reviewId: string;
  replacementPagePaths: string[];
}): Promise<{ pages: number; path: string; mime: string }> {
  const payload = await updateReviewOriginal({
    ...input,
    mode: 'attach_review_original',
    replacementPagePaths: input.replacementPagePaths,
  });
  return {
    pages: Number(payload.pages ?? 0),
    path: String(payload.review_original_path ?? ''),
    mime: String(payload.review_original_mime ?? 'application/pdf'),
  };
}

export async function rejectDocumentImportReview(input: {
  reviewId: string;
  reason: string;
}): Promise<void> {
  const deviceId = await getDeviceId();
  const assertion = getActiveOperatorAssertion();
  if (assertion) {
    await activateSharedTabletOperatorGrant({
      grantId: assertion.grant_id,
      grantToken: assertion.grant_token,
      tenantPersonnelId: assertion.tenant_personnel_id,
      deviceId,
    });
  }
  const { error } = await getSupabase().rpc('reject_document_import_review_v1', {
    p_review_id: input.reviewId,
    p_reason: input.reason,
    p_device_id: deviceId,
  });
  if (error) throw error;
}
